import client from "../../external/open-api";
import { components } from "../../external/schema/order-link-api-schema";
import { ApplicationError } from "../../models/errors/application-error";
import BaseService from "../base-service";

export type WhatsappConversation = components["schemas"]["WhatsappConversationDTO"];
export type WhatsappMessage = components["schemas"]["WhatsappMessageDTO"];
export type WhatsappConversationList = components["schemas"]["GetConversationsResponse"];

export default class WhatsappCommunicationsService extends BaseService {
  async getConversations(): Promise<WhatsappConversationList> {
    const { data, error } = await this.withRetry(() =>
      client.GET("/v{version}/api/whatsapp/conversations", {
        params: {
          path: { version: "1" },
          query: {
            "Paging.FetchAll": true,
          },
        },
      })
    );

    if (!data?.success || error || !data.data) {
      throw new ApplicationError("Failed to fetch conversations");
    }

    return data.data;
  }

  async getMessages(conversationId: number): Promise<WhatsappMessage[]> {
    const { data, error } = await this.withRetry(() =>
      client.GET("/v{version}/api/whatsapp/conversations/{conversationId}/messages", {
        params: {
          path: { version: "1", conversationId },
        },
      })
    );

    if (!data?.success || error) {
      throw new ApplicationError("Failed to fetch conversation messages");
    }

    return data.data ?? [];
  }

  async replyToConversation(conversationId: number, message: string): Promise<WhatsappMessage> {
    const { data, error } = await this.withRetry(() =>
      client.POST("/v{version}/api/whatsapp/conversations/{conversationId}/reply", {
        params: {
          path: { version: "1", conversationId },
        },
        body: {
          message,
        },
      })
    );

    if (!data?.success || error || !data.data) {
      throw new ApplicationError("Failed to send message");
    }

    return data.data;
  }

  async downloadMedia(mediaId: string, mimeType?: string | null): Promise<Blob> {
    const { data, error, response } = await this.withRetry(() =>
      client.GET("/v{version}/api/whatsapp/media/{mediaId}", {
        params: {
          path: { version: "1", mediaId },
        },
      })
    );

    if (response?.status === 204) {
      throw new ApplicationError("No media found for this attachment");
    }

    if (!data?.data?.file || error) {
      throw new ApplicationError("Failed to download media attachment");
    }

    const byteCharacters = atob(data.data.file);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType ?? "application/octet-stream" });
  }
}